<template>
  <form class="nx-form nx-relation-card" :class="{ 'nx-active': active }" :data-vv-scope="scope">
      <v-progress-linear
        v-show="loading"
        indeterminate
        background-opacity="0"
        height="2"
      />

      <v-layout class="row wrap px-3">
        <v-flex class="xs12">
          <nx-content-form-selector
            :id="`form-field-${scope}-destContent`"
            :disabled="loading || disabled"
            :label="$t('message.form.fields.addRelation')"
            :allow-non-published="allowNonPublished"
            :excluded-content-ids="excludedEntityIds"
            :value="draft.destContent"
            :open-modal-editor="openModalEditor"
            :wizard="wizard"
            :type="types"
            name="destContent"
            validation="required"
            @input="relationDeleted(uuid, ...$event)"
          />
          <component
            :is="textComponent"
            :id="`form-field-${scope}-title`"
            name="title"
            :disabled="loading || disabled"
            :label="$t('message.form.fields.title')"
            :value="draft.title"
            v-validate="draft.destContent ? 'required' : ''"
            v-bind="textComponentProps"
          />
          <nx-form-select
            v-if="showLinkTarget"
            :disabled="loading || disabled"
            :label="$t('message.form.fields.linkTarget')"
            :value="draft.linkTarget"
            name="linkTarget"
            :items="showLinkTargetOptions"
            validation="required"
            :default="showLinkTargetDefault"
          ></nx-form-select>
        </v-flex>
      </v-layout>
  </form>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import isObject from 'lodash/isObject';
import FormScope from '@/modules/form/FormScope';
import NxContentFormSelector from '@/modules/content/selector/FormSelector';
import NxFormText from '@/modules/form/components/FormText';
import NxFormTextarea from '@/modules/form/components/FormTextarea';
import api from '@/modules/relation/api/';
import { getPopulateFieldsForRelation } from '@/modules/entity/api';
import { isArray, isEmpty } from 'lodash';
import RelationDeletionMixin from '@/modules/relation/mixins/relation-deletion.mixin';

export default {
  name: 'nx-relation-card-form',
  extends: FormScope,
  mixins: [RelationDeletionMixin],
  components: {
    NxFormText,
    NxFormTextarea,
    NxContentFormSelector,
  },
  inject: {
    $formField: {
      default: null,
    },
    $moduleType: {
      default: null,
    },
    $getParentEditor: {
      default: null,
    },
  },
  props: {
    types: {
      type: Array,
    },
    uuid: {
      type: String,
      required: true,
    },
    relationName: {
      type: String,
      default: null,
    },
    relationType: {
      type: String,
      default: null,
    },
    allowNonPublished: {
      type: Boolean,
      default: false,
    },
    excludedEntityIds: {
      type: Array,
      default: () => ([]),
    },
    disabled: {
      type: Boolean,
    },
    disableCrop: {
      type: Boolean,
    },
    wizard: {
      type: [String, Array],
    },
    clippingSelectorAdditionalFilters: {
      type: Object,
      default: () => ({}),
    },
    beforeCreateClippingAliases: {
      type: Array,
      default: () => ([]),
    },
    usePopulateFieldsCmsApiService: {
      type: Boolean,
    },
    openModalEditor: {
      type: Boolean,
      default: false,
    },
    useTextarea: {
      type: Boolean,
      default: false,
    },
    showLinkTarget: {
      type: Boolean,
      default: false,
    },
    showLinkTargetOptions: {
      type: Array,
      default: () => (['']),
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    updateSpecificField() {
      return {
        destContent: this.updateDestContent,
      };
    },
    textComponent() {
      return this.useTextarea ? NxFormTextarea : NxFormText;
    },
    textComponentProps() {
      return this.useTextarea ? { rows: 1, autoGrow: true } : {};
    },
    showLinkTargetDefault() {
      return this.showLinkTargetOptions ? this.showLinkTargetOptions[0] : '';
    },
  },
  beforeDestroy() {
    this.$nextTick(() => {
      this.$validator.errors.clear(this.scope);
    });
  },
  methods: {
    /**
     * Updates a field from the draft with the given value.
     * If a more specific handler is registered on `updateSpecificField`, this handler will be
     * called and its result will be used as the new draft value.
     *
     * @param {string}   field
     * @param {*}        value
     * @param {Array<*>} args
     *
     * @return {Promise<*>} A promise that resolves with the updated draft value.
     */
    async updateField(field, value, ...args) {
      let draft = {};
      const { defaultUpdate = false } = isObject(args[0]) ? args[0] : {};
      if (this.updateSpecificField[field]) {
        draft = await this.updateSpecificField[field](value, ...args);
      } else {
        draft = cloneDeep(this.value);
        draft[field] = value;
      }

      this.$emit(defaultUpdate ? 'default' : 'input', this.uuid, draft);
      if (!defaultUpdate) {
        // Wait next tick to validate card scope and relation card errors
        this.$nextTick(() => {
          this.$validator.validate(`${this.scope}.*`);
        });
      }
      return draft;
    },
    /**
     * @deprecated
     */
    async getLegacyPopulatedFields(contentId, triggerField, extraParams = {}) {
      const results = this.relationType !== 'configGenerated'
        ? await this.getRelationFields(contentId, extraParams)
        : await this.getConfigGeneratedFields('destContent', [contentId], extraParams);
      if (isArray(results) && !isEmpty(results)) {
        return results[0];
      }
      return [];
    },
    /**
     * @deprecated
     */
    async getRelationFields(contentId, extraParams = {}) {
      let parentContentId = null;
      if (this.$getParentEditor
        && this.$getParentEditor().type
        && this.$getParentEditor().type !== 'composition'
      ) {
        parentContentId = this.$getParentEditor().id;
      }
      return api.getFields({
        relationType: this.relationType,
        entityIds: [contentId],
        ...extraParams,
      }, parentContentId);
    },
    /**
     * @deprecated
     */
    async getConfigGeneratedFields(triggerField, entityIds) {
      // Only works with board modules
      if (!this.$formField || !this.$moduleType || !this.$moduleType()) {
        return [];
      }
      return api.getModuleConfigGeneratedFields(this.$moduleType(), this.$formField, {
        triggerField,
        entityIds: entityIds.map(entity => ((isObject(entity) && typeof entity.id !== 'undefined') ? entity.id : entity)),
      });
    },

    async updateDestContent(contentId, content = false, extraParams = {}) {
      if (!contentId) {
        return null;
      }

      this.loading = true;
      const results = this.usePopulateFieldsCmsApiService
        ? await getPopulateFieldsForRelation(contentId, content ? content.type : undefined, {
          triggerPopulationField: 'destContent',
          contextEntity: this.$getParentEditor ? this.$getParentEditor().type || this.$getParentEditor().entity : null,
          contextEntityId: this.$getParentEditor ? this.$getParentEditor().id : null,
          relationType: this.relationType,
          draft: this.$getParentEditor ? this.$getParentEditor().draft : {},
        })
        : await this.getLegacyPopulatedFields(contentId, 'destContent', extraParams);
      this.loading = false;

      if (!isEmpty(results)) {
        return results;
      }

      // Couldn't get the relation fields. Update the content id at least.
      const value = cloneDeep(this.draft);
      value.destContent = content || { id: contentId };
      return value;
    },
  },
};
</script>

<style lang="stylus">
.nx-relation-card > .container > .v-progress-linear,
.nx-relation-card.container > .v-progress-linear {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  margin: 0;
}
</style>
